import { useEffect } from "react";
import { getMetaData } from "../../utils/Constant";

const GIFFormatGuide = () => {

  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("gif-meta-data");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description;
  }, []);

  return (
    <div className="container mt-5 mb-5">
      <article className="bg-white rounded-4 shadow-lg border-0 overflow-hidden">
        {/* Header Section */}
        <header
          className="text-center py-5 px-3 position-relative"
          style={{
            background: "linear-gradient(135deg, #fd7e14 0%, #ffc107 100%)",
            color: "#fff",
          }}
        >
          <h1 className="display-5 fw-bold mb-3 position-relative">
            GIF Format: Still Popular or Time for an Upgrade?
          </h1>
          <p className="lead mb-0 position-relative fs-4">
            When to embrace the classics, when to upgrade, and why GIFs still matter
          </p>
          <div className="mt-4 position-relative">
            <span className="badge bg-light text-warning fs-6 me-2 p-2">
              Animation
            </span>
            <span className="badge bg-light text-warning fs-6 me-2 p-2">
              Memes & Social
            </span>
            <span className="badge bg-light text-warning fs-6 p-2">
              Web History
            </span>
          </div>
        </header>

        {/* Content Section */}
        <section className="p-4 p-md-5">
          {/* Introduction */}
          <div
            className="alert alert-warning border-0 rounded-3 shadow-sm"
            role="alert"
          >
            <div className="d-flex">
              <div className="me-3 fs-4">🎭</div>
              <div>
                <p className="fs-5 lh-lg mb-0">
                  GIFs have been bringing the internet to life since 1987, and despite 
                  technically superior alternatives, they're still everywhere. From reaction 
                  memes to loading animations, understanding when to use GIFs (and when to 
                  avoid them) is crucial for every web developer and content creator.
                </p>
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="alert alert-info border-0 rounded-3 mb-5">
            <div className="d-flex">
              <div className="me-3 fs-5">💡</div>
              <div>
                <strong>Historical context:</strong> The GIF format was created by CompuServe on June 15, 1987 - 
                two years before Tim Berners-Lee proposed the World Wide Web. The GIF89a specification 
                added animation support in 1989, and Netscape 2.0+ introduced the looping extension that made infinite animations possible.
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="card border-0 shadow-sm mb-5">
            <div className="card-header bg-warning text-dark py-3">
              <h2 className="h4 mb-0 fw-bold">What we're covering</h2>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "Why GIFs are still everywhere",
                      "Where GIFs actually work well", 
                      "The technical limitations",
                      "File size realities",
                      "Perfect use cases",
                      "Modern alternatives comparison",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 1}`}
                          className="text-decoration-none text-warning hover-text-dark"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "Creation & optimization",
                      "Performance impact",
                      "When to use GIFs today",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 7}`}
                          className="text-decoration-none text-warning hover-text-dark"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Sections 1-8 remain the same */}
          {/* ... */}

          {/* Section 9 - When to Use GIFs Today */}
          <div id="section-9" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-warning border-bottom pb-2">
              <span
                className="bg-warning text-dark rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                9
              </span>
              When to use GIFs in 2024: The practical decision guide
            </h2>
            
            <div className="row g-4">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0 fw-bold">✅ Use GIF For</h5>
                  </div>
                  <div className="card-body">
                    <div className="text-center mb-3">
                      <span className="fs-1">👍</span>
                    </div>
                    <ul className="text-secondary">
                      <li><strong>Reaction memes in chat</strong> - cultural compatibility matters</li>
                      <li><strong>Small loading animations</strong> - under 10KB spinners</li>
                      <li><strong>Email marketing</strong> - maximum compatibility needed</li>
                      <li><strong>Social media posts</strong> - Twitter, Reddit, etc.</li>
                      <li><strong>Forum signatures</strong> - old-school web culture</li>
                      <li><strong>Pixel art animations</strong> - limited colors work well</li>
                    </ul>
                    <div className="mt-3 p-2 bg-light rounded">
                      <small className="text-muted">
                        <strong>Best for:</strong> Short, simple animations where universal compatibility is more important than file size.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-warning text-dark py-3">
                    <h5 className="mb-0 fw-bold">⚠️ Consider Alternatives</h5>
                  </div>
                  <div className="card-body">
                    <div className="text-center mb-3">
                      <span className="fs-1">🤔</span>
                    </div>
                    <ul className="text-secondary">
                      <li><strong>Screen recordings</strong> - use MP4/WebM instead</li>
                      <li><strong>Product demonstrations</strong> - video offers better quality</li>
                      <li><strong>UI animations</strong> - consider SVG or Lottie</li>
                      <li><strong>Marketing hero sections</strong> - performance matters</li>
                      <li><strong>Educational content</strong> - video allows pausing</li>
                      <li><strong>Mobile-heavy audiences</strong> - data usage concerns</li>
                    </ul>
                    <div className="mt-3 p-2 bg-light rounded">
                      <small className="text-muted">
                        <strong>Consider:</strong> WebP for better compression, MP4 for video-like content, SVG for scalability.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-danger text-white py-3">
                    <h5 className="mb-0 fw-bold">❌ Avoid GIF For</h5>
                  </div>
                  <div className="card-body">
                    <div className="text-center mb-3">
                      <span className="fs-1">👎</span>
                    </div>
                    <ul className="text-secondary">
                      <li><strong>Video content</strong> - 90% larger files</li>
                      <li><strong>High-quality animations</strong> - color limitations</li>
                      <li><strong>Large hero images</strong> - performance impact</li>
                      <li><strong>Background elements</strong> - unnecessary bloat</li>
                      <li><strong>E-commerce product views</strong> - use video instead</li>
                      <li><strong>Professional portfolios</strong> - quality matters</li>
                    </ul>
                    <div className="mt-3 p-2 bg-light rounded">
                      <small className="text-muted">
                        <strong>Use instead:</strong> MP4 for video, WebP/APNG for animations, SVG for vectors.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Decision Framework */}
            <div className="mt-5 p-4 bg-light rounded-3">
              <h5 className="fw-bold mb-3">Quick Decision Framework:</h5>
              <div className="row text-center">
                <div className="col-md-3 mb-3">
                  <div className="p-3 border rounded bg-white">
                    <div className="fs-4 mb-2">⏱️</div>
                    <h6 className="fw-bold">Duration</h6>
                    <p className="small mb-1"><strong>Under 5s:</strong> GIF okay</p>
                    <p className="small mb-0"><strong>Over 5s:</strong> Use video</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="p-3 border rounded bg-white">
                    <div className="fs-4 mb-2">🎨</div>
                    <h6 className="fw-bold">Colors</h6>
                    <p className="small mb-1"><strong>Under 64:</strong> GIF okay</p>
                    <p className="small mb-0"><strong>Over 64:</strong> Use WebP/APNG</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="p-3 border rounded bg-white">
                    <div className="fs-4 mb-2">💾</div>
                    <h6 className="fw-bold">File Size</h6>
                    <p className="small mb-1"><strong>Under 1MB:</strong> GIF okay</p>
                    <p className="small mb-0"><strong>Over 1MB:</strong> Use alternatives</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="p-3 border rounded bg-white">
                    <div className="fs-4 mb-2">📱</div>
                    <h6 className="fw-bold">Audience</h6>
                    <p className="small mb-1"><strong>Social/media:</strong> GIF okay</p>
                    <p className="small mb-0"><strong>Professional:</strong> Use modern formats</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="alert alert-info mt-4" role="alert">
              <strong>Remember:</strong> The right choice depends on your specific use case. 
              When in doubt, test both GIF and modern alternatives with your actual audience and measure the results.
            </div>
          </div>

          {/* Section 10 - FAQ */}
          {/* <div id="section-10" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-warning border-bottom pb-2">
              <span
                className="bg-warning text-dark rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                10
              </span>
              Your GIF Questions Answered
            </h2>
            
            <div className="accordion" id="gifFAQ">
              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                    Do GIFs really work on every browser and device?
                  </button>
                </h3>
                <div id="faq1" className="accordion-collapse collapse" data-bs-parent="#gifFAQ">
                  <div className="accordion-body">
                    <p><strong>Virtually all modern browsers</strong> support GIFs, but there are some exceptions:</p>
                    <ul>
                      <li><strong>Text-based browsers</strong> like Lynx don't display images</li>
                      <li><strong>Some older mobile browsers</strong> may have limited GIF support</li>
                      <li><strong>Screen readers and accessibility tools</strong> may not process animations</li>
                    </ul>
                    <p>For practical purposes, you can assume GIF support in any graphical web browser made in the last 20 years. However, it's not technically "every browser ever made."</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                    What's the difference between GIF transparency and modern alpha channels?
                  </button>
                </h3>
                <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#gifFAQ">
                  <div className="accordion-body">
                    <p><strong>GIF uses 1-bit transparency:</strong> Each pixel is either fully transparent or fully opaque. This creates jagged, aliased edges.</p>
                    <p><strong>Modern formats (APNG, WebP) use 8-bit alpha channels:</strong> Each pixel can have 256 levels of transparency, allowing for smooth, anti-aliased edges and semi-transparent effects.</p>
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <strong>GIF Transparency:</strong>
                        <ul>
                          <li>Binary (on/off)</li>
                          <li>Jagged edges</li>
                          <li>One transparent color</li>
                          <li>No smooth gradients</li>
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <strong>Alpha Channel:</strong>
                        <ul>
                          <li>256 transparency levels</li>
                          <li>Smooth, anti-aliased edges</li>
                          <li>Partial transparency</li>
                          <li>Professional quality</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                    Why are APNG files sometimes larger than GIFs?
                  </button>
                </h3>
                <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#gifFAQ">
                  <div className="accordion-body">
                    <p>APNG files can be larger than GIFs for several reasons:</p>
                    <ul>
                      <li><strong>24-bit color vs 8-bit:</strong> APNG stores 16.7 million colors vs GIF's 256 colors</li>
                      <li><strong>Alpha channel data:</strong> 8-bit transparency adds significant data</li>
                      <li><strong>Less aggressive compression:</strong> APNG prioritizes quality over smallest file size</li>
                      <li><strong>Frame optimization:</strong> GIFs can use frame disposal methods that APNG doesn't support as efficiently</li>
                    </ul>
                    <p>However, for complex animations with many colors, APNG often produces better quality at similar or smaller file sizes. The trade-off is worth it for professional animations.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq4">
                    What's the actual browser support for animated WebP vs APNG?
                  </button>
                </h3>
                <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#gifFAQ">
                  <div className="accordion-body">
                    <p><strong>Animated WebP Support:</strong></p>
                    <ul>
                      <li>Chrome 17+ (2012)</li>
                      <li>Firefox 65+ (2019)</li>
                      <li>Edge 18+ (2018)</li>
                      <li>Safari 14+ (2020)</li>
                      <li>Opera 12.1+ (2012)</li>
                    </ul>
                    
                    <p><strong>APNG Support:</strong></p>
                    <ul>
                      <li>Firefox 3+ (2008)</li>
                      <li>Chrome 59+ (2017)</li>
                      <li>Safari 8+ (2014)</li>
                      <li>Edge 79+ (2020)</li>
                      <li>Opera 46+ (2017)</li>
                    </ul>
                    
                    <p><strong>Conclusion:</strong> APNG currently has slightly wider support, but both formats are well-supported in modern browsers. Use feature detection for critical applications.</p>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Final CTA */}
          <div
            className="text-center mt-5 p-5 rounded-4"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            }}
          >
            <h2 className="h1 fw-bold mb-3 text-warning">
              Ready to master GIF optimization?
            </h2>
            <p className="fs-5 text-secondary mb-4">
              Get our curated collection of GIF sample files and see the performance trade-offs for yourself.
            </p>
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
              <a
                href="https://samplefiles.dev/sample-images/sample-gif"
                className="btn btn-warning btn-lg px-5 py-3 shadow hover-lift"
                target="_blank"
                rel="noopener noreferrer"
              >
                🎭 Get GIF Sample Files
              </a>
              <a
                href="#section-1"
                className="btn btn-outline-warning btn-lg px-5 py-3"
              >
                📖 Read This Again
              </a>
            </div>
            <p className="mt-3 text-muted small">
              Various sizes and optimization levels - perfect for testing and comparison.
            </p>
          </div>
        </section>
      </article>
    </div>
  );
};

export default GIFFormatGuide;

//                       "Common questions answered",
